// useCuratedLibrary.js
// ─────────────────────────────────────────────────────────────────────────────
// Responsabilidad: alimentar la LibraryScreen del juego desde la DB.
//
// Flujo:
//   1. sessionStorage cache (TTL 5 min) → respuesta instantánea
//   2. /api/minigames/cinema/library/songs + /library/videos → datos reales
//
// Y nada más. Hasta el 05-10-2026 había un tercer escalón —ContentLibrary.js,
// un catálogo escrito a mano— que rellenaba la pantalla cuando la base estaba
// vacía. El problema es que aquellas piezas NO SE PODÍAN JUGAR: no tienen letra
// sincronizada ni preguntas, así que salían con un candado y un «Pronto» y lo
// único que hacían era empujar hacia abajo las que sí abren. Un catálogo de
// relleno no es resistencia a fallos, es ruido con buena intención.
//
// El hook normaliza ambas fuentes al mismo shape que LibraryScreen consume:
//   { id, label, icon, gradient, items: [...], fromDB: bool }
//
// items de música:
//   { videoId, title, artist, song, category, difficulty, curatedId?, fromDB }
// items de video:
//   { videoId, title, channel, topic, level, curatedId?, fromDB }
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../api/apiClient';

const API_BASE = '/api/minigames/cinema/library';
// La version va en el nombre a proposito: al cambiar como se arma el catalogo,
// la cache guardada deja de valer. Sin subir el numero, quien la tuviera de
// hace cinco minutos seguiria viendo el catalogo encogido hasta que caduque.
const CACHE_SONGS = 'nsl_lib_songs_v4';
const CACHE_VIDS = 'nsl_lib_videos_v4';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

// ── Metadata de categorías (unifica los IDs de la DB con metadatos UI) ────────

const CATEGORY_META = {
    // Music
    pop: { label: 'Pop', icon: '🎵', gradient: 'linear-gradient(135deg,#7C3AED,#06B6D4)' },
    rock: { label: 'Rock', icon: '🎸', gradient: 'linear-gradient(135deg,#F59E0B,#EF4444)' },
    soul: { label: 'R&B / Soul', icon: '🎤', gradient: 'linear-gradient(135deg,#10B981,#3B82F6)' },
    indie: { label: 'Indie / Alt', icon: '🌟', gradient: 'linear-gradient(135deg,#06B6D4,#8B5CF6)' },
    classic: { label: 'Clásicos', icon: '🎼', gradient: 'linear-gradient(135deg,#8B5CF6,#EC4899)' },
    country: { label: 'Country', icon: '🤠', gradient: 'linear-gradient(135deg,#D97706,#92400E)' },
    electronic: { label: 'Electronic', icon: '⚡', gradient: 'linear-gradient(135deg,#6366F1,#06B6D4)' },
    folk: { label: 'Folk', icon: '🎻', gradient: 'linear-gradient(135deg,#16A34A,#D97706)' },
    // Video topics
    physics: { label: 'Física & Ciencia', icon: '⚛️', gradient: 'linear-gradient(135deg,#3B82F6,#06B6D4)' },
    ai: { label: 'IA & Tecnología', icon: '🧠', gradient: 'linear-gradient(135deg,#8B5CF6,#EC4899)' },
    economics: { label: 'Economía', icon: '📈', gradient: 'linear-gradient(135deg,#10B981,#F59E0B)' },
    history: { label: 'Historia', icon: '🏛️', gradient: 'linear-gradient(135deg,#F59E0B,#EF4444)' },
    psychology: { label: 'Psicología', icon: '🧬', gradient: 'linear-gradient(135deg,#EC4899,#8B5CF6)' },
    business: { label: 'Business', icon: '💼', gradient: 'linear-gradient(135deg,#3B82F6,#1D4ED8)' },
    tech: { label: 'Tecnología', icon: '💻', gradient: 'linear-gradient(135deg,#6366F1,#7C3AED)' },
    science: { label: 'Ciencia', icon: '🔬', gradient: 'linear-gradient(135deg,#10B981,#3B82F6)' },
    culture: { label: 'Cultura', icon: '🎨', gradient: 'linear-gradient(135deg,#EC4899,#F59E0B)' },
    health: { label: 'Salud', icon: '🏥', gradient: 'linear-gradient(135deg,#10B981,#06B6D4)' },
    environment: { label: 'Ambiente', icon: '🌿', gradient: 'linear-gradient(135deg,#16A34A,#10B981)' },
    politics: { label: 'Política', icon: '🏛️', gradient: 'linear-gradient(135deg,#6366F1,#3B82F6)' },
    sports: { label: 'Deportes', icon: '⚽', gradient: 'linear-gradient(135deg,#F59E0B,#EF4444)' },
    general: { label: 'General', icon: '📚', gradient: 'linear-gradient(135deg,#6B7280,#374151)' },
};

const metaFor = (id) => CATEGORY_META[id] ?? CATEGORY_META.general;

// ── sessionStorage helpers ─────────────────────────────────────────────────

function cacheRead(key) {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const { ts, data } = JSON.parse(raw);
        if (Date.now() - ts > CACHE_TTL) { sessionStorage.removeItem(key); return null; }
        return data;
    } catch {
        return null;
    }
}

function cacheWrite(key, data) {
    try { sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data })); } catch { }
}

// ── Transformadores API → shape interno ───────────────────────────────────

/**
 * Agrupa un array plano de ítems por una clave de categoría
 * y los convierte al shape { id, label, icon, gradient, items, fromDB }.
 */
function groupByCategory(items, categoryKey) {
    const map = new Map();
    for (const item of items) {
        const cat = item[categoryKey] || 'general';
        if (!map.has(cat)) map.set(cat, []);
        map.get(cat).push(item);
    }
    return [...map.entries()].map(([catId, catItems]) => {
        const meta = metaFor(catId);
        return {
            id: catId,
            label: meta.label,
            icon: meta.icon,
            gradient: meta.gradient,
            items: catItems,
            fromDB: true,
        };
    });
}

/** Normaliza un ítem de canción de la API al shape del juego */
function normalizeSong(s) {
    return {
        // Campos que la UI y los hooks del juego necesitan
        videoId: s.youtube_id,
        title: s.title,
        artist: s.artist,
        song: s.title,          // alias — useMusicGame usa .song
        category: s.category,
        difficulty: s.difficulty,
        duracion: s.duration,     // segundos, o null si la pieza no da para saberlo
        // Identificadores de origen
        curatedId: s.id,             // ← Phase 3 lo usará para loadMusicFromDB
        fromDB: true,
    };
}

/** Normaliza un ítem de video de la API al shape del juego */
function normalizeVideo(v) {
    return {
        videoId: v.youtube_id,
        title: v.title,
        channel: v.channel || '',
        topic: v.topic || 'general',
        level: v.level,
        duracion: v.duration,     // segundos, o null si la pieza no da para saberlo
        language: v.language,
        // Identificadores de origen
        curatedId: v.id,             // ← Phase 4 lo usará para loadDocFromDB
        fromDB: true,
    };
}

// ── Hook principal ─────────────────────────────────────────────────────────

/**
 * @param {string} token  JWT del usuario autenticado
 *
 * @returns {{
 *   musicCategories: Array,  // categorías de música con .items
 *   videoCategories: Array,  // categorías de video con .items
 *   loading:   boolean,
 *   fromDB:    boolean,      // true si los datos vienen de la DB
 *   refresh:   () => void,   // fuerza un re-fetch ignorando cache
 * }}
 */
export function useCuratedLibrary(token) {
    const [musicCategories, setMusicCategories] = useState(null);
    const [videoCategories, setVideoCategories] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fromDB, setFromDB] = useState(false);

    // Ref para evitar race conditions si el componente se desmonta durante el fetch
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    const fetchLibrary = useCallback(async (skipCache = false) => {
        if (!mountedRef.current) return;
        setLoading(true);

        // 1. Cache hit
        if (!skipCache) {
            const cachedMusic = cacheRead(CACHE_SONGS);
            const cachedVideo = cacheRead(CACHE_VIDS);
            if (cachedMusic && cachedVideo) {
                if (mountedRef.current) {
                    setMusicCategories(cachedMusic);
                    setVideoCategories(cachedVideo);
                    setFromDB(true);
                    setLoading(false);
                }
                return;
            }
        }

        // 2. Fetch desde la API usando apiClient (inyecta auth automáticamente y usa VITE_API_URL)
        try {
            const [songsRes, videosRes] = await Promise.all([
                apiClient.get(`${API_BASE}/songs?limit=200`).catch(() => ({ data: [] })),
                apiClient.get(`${API_BASE}/videos?limit=200`).catch(() => ({ data: [] })),
            ]);

            const rawSongs = songsRes.data || [];
            const rawVideos = videosRes.data || [];

            // Solo canciones con contenido sincronizado real
            const songs = rawSongs.filter(s => s.has_content).map(normalizeSong);
            const videos = rawVideos.filter(v => v.has_content).map(normalizeVideo);

            const musicCats = groupByCategory(songs, 'category');
            const videoCats = groupByCategory(videos, 'topic');

            //  Vacío también se guarda. Si la biblioteca está vacía de verdad,
            //  volver a preguntarlo en cada visita no cambia la respuesta.
            cacheWrite(CACHE_SONGS, musicCats);
            cacheWrite(CACHE_VIDS, videoCats);

            if (mountedRef.current) {
                setMusicCategories(musicCats);
                setVideoCategories(videoCats);
                setFromDB(songs.length > 0 || videos.length > 0);
            }
        } catch (err) {
            //  Sin red no hay catálogo: la pantalla lo dice en vez de enseñar
            //  piezas que no abrirían.
            console.warn('[useCuratedLibrary] no se pudo cargar la biblioteca:', err.message);
            if (mountedRef.current) {
                setMusicCategories([]);
                setVideoCategories([]);
                setFromDB(false);
            }
        }

        if (mountedRef.current) setLoading(false);
    }, [token]);

    // Fetch inicial
    useEffect(() => { fetchLibrary(); }, [fetchLibrary]);

    return {
        musicCategories: musicCategories ?? [],
        videoCategories: videoCategories ?? [],
        loading,
        fromDB,
        refresh: () => fetchLibrary(true),
    };
}